import React, { useEffect, useState, useCallback } from 'react';
import {
  X, Building2, Loader2, AlertCircle, Home, Users, Briefcase, FileText, MessageSquare, Paperclip, MapPin, Activity,
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { formatDocument, formatCNPJ } from '../utils/formatDocument';
import { useAuth } from '../hooks/useAuth';
import { useBusinessUnit } from '../hooks/useBusinessUnit';
import { useAppPermissions } from '../hooks/useAppPermissions';
import EmpresaInlineEditPanel from './EmpresaInlineEditPanel';
import EntityLink from './shared/EntityLink';
import IntakeZone from './intake/IntakeZone';
import KnowledgeSection from './shared/KnowledgeSection';
import EmpresaLaunchesTab from './launches/EmpresaLaunchesTab';
import UnifiedTimeline from './UnifiedTimeline';

/**
 * Onda 8 abr/26 — EmpresaQuickViewModal.
 * Substitui o deep-link para Dashboard ao clicar em uma empresa — abre ficha
 * compacta com dados editáveis + imóveis + sócios + ofertas + documentos + timeline.
 */
export default function EmpresaQuickViewModal({ empresaId, onClose }) {
  const { user } = useAuth();
  const { availableUnits, isLoading: buLoading } = useBusinessUnit();
  const { permissions } = useAppPermissions();
  const [empresa, setEmpresa] = useState(null);
  const [imoveis, setImoveis] = useState([]);
  const [socios, setSocios] = useState([]);
  const [docs, setDocs] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [ofertas, setOfertas] = useState([]);
  const [decisores, setDecisores] = useState([]);
  const [linkedContacts, setLinkedContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('lands');

  // Ajuste 1 Gemini: tabs só aparecem quando context resolveu E user tem AMBAS permissions.
  // Se BU ainda carregando OU user só tem 1 permission → render legacy mode (sem tabs).
  const hasLandsRead    = permissions.includes('lands.read');
  const hasLaunchesRead = permissions.includes('launches.read');
  const showTabs = !buLoading
    && Array.isArray(availableUnits)
    && hasLandsRead
    && hasLaunchesRead;

  const loadAll = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    setError(null);
    try {
      const [empR, imoR, socR, docR, tlR, ofR, decR, lcR] = await Promise.all([
        supabase.from('empresas').select('*').eq('id', empresaId).single(),
        supabase.from('v_empresa_imoveis').select('*').eq('empresa_id', empresaId),
        supabase.from('socios')
          .select('*, contato:contatos!socios_socio_contato_id_fkey(id, first_name, last_name, cpf), empresa_socia:empresas!socios_socio_empresa_id_fkey(id, empresa, company_name)')
          .eq('empresa_id', empresaId).eq('is_active', true)
          .order('percentual_participacao', { ascending: false }),
        supabase.from('v_entity_documents').select('*').eq('entity_type', 'empresa').eq('entity_id', empresaId),
        supabase.from('empresa_context').select('*').eq('empresa_id', empresaId).order('created_at', { ascending: false }).limit(10),
        supabase.from('ofertas').select('id, nome, status').eq('empresa_id', empresaId).not('status', 'ilike', '9.%').order('updated_at', { ascending: false }).limit(10),
        supabase.from('contatos')
          .select('id, first_name, last_name, title, eh_decisor')
          .eq('empresa_id', empresaId).eq('eh_decisor', true).order('first_name'),
        supabase.from('v_entity_contacts').select('*').eq('entity_type', 'empresa').eq('entity_id', empresaId),
      ]);
      if (empR.error) throw empR.error;
      setEmpresa(empR.data);
      setImoveis(imoR.data || []);
      setSocios(socR.data || []);
      setDocs(docR.data || []);
      setTimeline(tlR.data || []);
      setOfertas(ofR.data || []);
      setDecisores(decR.data || []);
      setLinkedContacts(lcR.data || []);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleUpdateEmpresa = async (id, patch) => {
    const who = user?.email || user?.id || null;
    const { error: err } = await supabase
      .from('empresas')
      .update({
        ...patch,
        updated_at: new Date().toISOString(),
        updated_by: patch.updated_by ?? who,
      })
      .eq('id', id);
    if (err) throw err;
    const { data } = await supabase.from('empresas').select('*').eq('id', id).single();
    if (data) setEmpresa(data);
  };

  const fmtArea = (v) => (v == null ? '—' : Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 0 }) + ' m²');
  const fmtPct = (v) => (v == null ? '—' : `${Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`);
  const fmtSize = (b) => (b == null ? '' : b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`);

  return (
    <div className="fixed inset-0 bg-black/50 z-[10004] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-[95vw] 2xl:max-w-7xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-slate-200 flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Building2 size={18} className="text-blue-600 flex-shrink-0" />
              <h2 className="text-base font-bold text-slate-800 truncate" title={empresa?.empresa || empresa?.company_name}>
                {empresa?.empresa || empresa?.company_name || 'Empresa'}
              </h2>
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
              {empresa?.papel_negocio && <span className="px-1.5 py-0.5 bg-slate-100 rounded">{empresa.papel_negocio}</span>}
              {empresa?.status_operacional && <span className="px-1.5 py-0.5 bg-slate-100 rounded">{empresa.status_operacional}</span>}
              {empresa?.cnpj && <span className="font-mono">{formatCNPJ(empresa.cnpj)}</span>}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 flex-shrink-0" title="Fechar">
            <X size={18} />
          </button>
        </div>

        {/* Tabs (ADR-018) — visíveis só quando user tem lands.read E launches.read */}
        {showTabs && (
          <div className="px-2 border-b border-slate-200 bg-slate-50 flex items-center gap-0.5 overflow-x-auto">
            <button
              type="button"
              onClick={() => setActiveTab('lands')}
              className={`px-2.5 py-1.5 text-xs flex items-center gap-1.5 transition-colors border-b-2 -mb-px ${
                activeTab === 'lands'
                  ? 'border-blue-500 text-blue-700 font-semibold'
                  : 'border-transparent text-slate-600 hover:bg-slate-100'
              }`}
            >
              <MapPin size={12} /> Áreas
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('launches')}
              className={`px-2.5 py-1.5 text-xs flex items-center gap-1.5 transition-colors border-b-2 -mb-px ${
                activeTab === 'launches'
                  ? 'border-violet-500 text-violet-700 font-semibold'
                  : 'border-transparent text-slate-600 hover:bg-slate-100'
              }`}
            >
              <Building2 size={12} /> Empreendimentos
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('timeline')}
              className={`px-2.5 py-1.5 text-xs flex items-center gap-1.5 transition-colors border-b-2 -mb-px ${
                activeTab === 'timeline'
                  ? 'border-emerald-500 text-emerald-700 font-semibold'
                  : 'border-transparent text-slate-600 hover:bg-slate-100'
              }`}
            >
              <Activity size={12} /> Timeline
            </button>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading && (
            <div className="flex items-center gap-2 text-xs text-slate-500 py-6 justify-center">
              <Loader2 size={14} className="animate-spin" /> Carregando empresa...
            </div>
          )}
          {!loading && error && (
            <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800 flex items-start gap-1.5">
              <AlertCircle size={12} className="flex-shrink-0 mt-0.5" /> {error}
            </div>
          )}

          {!loading && !error && empresa && (!showTabs || activeTab === 'lands') && (
            <>
              {/* Dados editáveis (reutiliza o painel que já existe no Dashboard) */}
              <EmpresaInlineEditPanel empresa={empresa} onUpdate={handleUpdateEmpresa} />

              {/* Imóveis */}
              <section>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5 flex items-center gap-1">
                  <Home size={10} /> Imóveis ({imoveis.length})
                </div>
                {imoveis.length === 0 ? (
                  <div className="text-xs text-slate-400 italic">Nenhum imóvel vinculado.</div>
                ) : (
                  <div className="bg-white border border-slate-200 rounded overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                        <tr>
                          <th className="px-2 py-1.5 text-left">Imóvel</th>
                          <th className="px-2 py-1.5 text-left">Matrícula</th>
                          <th className="px-2 py-1.5 text-right">Área</th>
                          <th className="px-2 py-1.5 text-right">%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {imoveis.map((r) => (
                          <tr key={r.imovel_id} className="border-t border-slate-100 hover:bg-sky-50">
                            <td className="px-2 py-1.5">
                              <EntityLink
                                entityType="imovel"
                                entityId={r.imovel_id}
                                label={r.imovel_nome || r.endereco || '—'}
                                className="max-w-[240px]"
                              />
                            </td>
                            <td className="px-2 py-1.5 font-mono text-slate-600 whitespace-nowrap">{r.matricula || '—'}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums text-slate-700 whitespace-nowrap">{fmtArea(r.area_terreno_m2)}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums text-slate-600 whitespace-nowrap">{fmtPct(r.percentual_participacao)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {/* Sócios */}
              <section>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5 flex items-center gap-1">
                  <Users size={10} /> Sócios ({socios.length})
                </div>
                {socios.length === 0 ? (
                  <div className="text-xs text-slate-400 italic">Nenhum sócio cadastrado.</div>
                ) : (
                  <div className="space-y-1">
                    {socios.map((s) => {
                      const isPF = !!s.contato;
                      const nome = isPF
                        ? [s.contato.first_name, s.contato.last_name].filter(Boolean).join(' ')
                        : (s.empresa_socia?.empresa || s.empresa_socia?.company_name || '—');
                      const doc = isPF ? s.contato?.cpf : null;
                      return (
                        <div key={s.id} className="bg-white border border-slate-200 rounded px-2 py-1.5 text-xs flex items-center gap-2">
                          <span className="flex-shrink-0">{isPF ? '👤' : '🏢'}</span>
                          {isPF && s.contato?.id ? (
                            <EntityLink entityType="contato" entityId={s.contato.id} label={nome} showIcon={false} className="flex-1" />
                          ) : !isPF && s.empresa_socia?.id ? (
                            <EntityLink entityType="empresa" entityId={s.empresa_socia.id} label={nome} showIcon={false} className="flex-1" />
                          ) : (
                            <span className="flex-1 truncate">{nome}</span>
                          )}
                          {doc && <span className="font-mono text-[10px] text-slate-400 whitespace-nowrap">{formatDocument(doc)}</span>}
                          {s.percentual_participacao != null && (
                            <span className="text-slate-500 tabular-nums whitespace-nowrap">{fmtPct(s.percentual_participacao)}</span>
                          )}
                          {s.tipo_socio && <span className="text-[10px] px-1 bg-slate-100 rounded">{s.tipo_socio}</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              {/* Decisores */}
              {decisores.length > 0 && (
                <section>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">
                    ⭐ Decisores ({decisores.length})
                  </div>
                  {decisores.map((d) => (
                    <div key={d.id} className="text-xs py-1 border-b border-slate-100 flex items-center gap-2">
                      <EntityLink
                        entityType="contato"
                        entityId={d.id}
                        label={[d.first_name, d.last_name].filter(Boolean).join(' ') || '—'}
                      />
                      <span className="text-slate-400 truncate">{d.title || '—'}</span>
                    </div>
                  ))}
                </section>
              )}

              {/* Contatos vinculados */}
              {linkedContacts.length > 0 && (
                <section>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">
                    🔗 Contatos vinculados ({linkedContacts.length})
                  </div>
                  {linkedContacts.map((lc, i) => (
                    <div key={`${lc.contato_id}-${lc.role}-${i}`} className="text-xs py-1 border-b border-slate-100 flex items-center gap-2 flex-wrap">
                      <EntityLink entityType="contato" entityId={lc.contato_id} label={lc.contato_nome || '—'} />
                      <span className="px-1.5 py-0.5 text-[9px] bg-purple-100 text-purple-700 rounded">{lc.role_label}</span>
                      {lc.is_primary && <span className="text-[8px] text-amber-600">★</span>}
                      {lc.eh_decisor && <span className="text-[9px]">⭐</span>}
                      {lc.notes && <span className="text-[10px] text-slate-400 truncate" title={lc.notes}>{lc.notes.slice(0, 60)}</span>}
                    </div>
                  ))}
                </section>
              )}

              {/* K2 26/abr/26: Seção Conhecimento (knowledge_atoms via v_entity_knowledge) */}
              <KnowledgeSection entityType="empresa" entityId={empresaId} />

              {/* Ofertas */}
              <section>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5 flex items-center gap-1">
                  <Briefcase size={10} /> Ofertas ativas ({ofertas.length})
                </div>
                {ofertas.length === 0 ? (
                  <div className="text-xs text-slate-400 italic">Nenhuma oferta ativa.</div>
                ) : (
                  <div className="space-y-1">
                    {ofertas.map((o) => (
                      <div key={o.id} className="bg-white border border-slate-200 rounded px-2 py-1.5 text-xs flex items-center gap-2">
                        <EntityLink entityType="oferta" entityId={o.id} label={o.nome} className="flex-1" />
                        {o.status && <span className="text-[10px] px-1.5 py-0.5 bg-orange-50 text-orange-700 rounded whitespace-nowrap">{o.status}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Documentos */}
              <section>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5 flex items-center gap-1">
                  <Paperclip size={10} /> Documentos ({docs.length})
                </div>
                {docs.length === 0 ? (
                  <div className="text-xs text-slate-400 italic">Nenhum documento vinculado.</div>
                ) : (
                  <div className="space-y-1">
                    {docs.map((d) => (
                      <div key={d.attachment_id} className="bg-white border border-slate-200 rounded px-2 py-1.5 text-xs flex items-center gap-2">
                        <FileText size={11} className="text-slate-400 flex-shrink-0" />
                        <span className="font-medium text-slate-800 truncate flex-1" title={d.filename || ''}>{d.filename || '(sem nome)'}</span>
                        {d.document_type && <span className="text-[10px] px-1 bg-slate-100 rounded whitespace-nowrap">{d.document_type}</span>}
                        {d.size_bytes != null && <span className="text-[10px] text-slate-500 whitespace-nowrap">{fmtSize(d.size_bytes)}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Timeline */}
              <section>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5 flex items-center gap-1">
                  <MessageSquare size={10} /> Timeline ({timeline.length})
                </div>
                {timeline.length === 0 ? (
                  <div className="text-xs text-slate-400 italic">Nenhum registro.</div>
                ) : (
                  <div className="space-y-1">
                    {timeline.map((t) => (
                      <div key={t.id} className="bg-white border border-slate-200 rounded p-2 text-xs">
                        <div className="flex items-center justify-between text-[10px] text-slate-500 mb-0.5">
                          <span>
                            {t.autor_tipo === 'ai' ? '🤖' : '👤'} {t.autor_id || '—'}
                            {t.tipo && <span className="ml-1 px-1 bg-slate-100 rounded">{t.tipo}</span>}
                          </span>
                          <span>{t.created_at ? new Date(t.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                        </div>
                        <div className="text-slate-700 whitespace-pre-wrap">{t.texto || <span className="text-slate-400 italic">(sem texto)</span>}</div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* TB-24.2 — UnifiedTimeline read-only (modo legacy, sem tabs) */}
              {!showTabs && (
                <section>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5 flex items-center gap-1">
                    <Activity size={10} /> Atividades
                  </div>
                  <UnifiedTimeline entityType="empresa" entityId={empresaId} limit={20} />
                </section>
              )}

              {/* IntakeZone */}
              <section>
                <IntakeZone
                  entityType="empresa"
                  entityId={empresaId}
                  defaultDocType="contrato_social"
                  onComplete={loadAll}
                />
              </section>
            </>
          )}

          {!loading && !error && empresa && showTabs && activeTab === 'launches' && (
            <EmpresaLaunchesTab empresaId={empresaId} />
          )}

          {!loading && !error && empresa && showTabs && activeTab === 'timeline' && (
            <UnifiedTimeline entityType="empresa" entityId={empresaId} limit={20} />
          )}
        </div>
      </div>
    </div>
  );
}
